import React, { useEffect, useState } from 'react';

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';

import { PopupTransition } from 'components/@extended/Transitions';
import IncidentReportService from 'services/incident-report.service';
import SnackbarNotify from 'utils/snackbar/SnackbarNotify';
import { capitalizeFirstLetterMulti } from 'utils/Helpers';
import {
    INCIDENT_PARTY_SUBJECT_ROLE,
    errorMessageFrom,
    humanize
} from 'utils/helpers/guard-report.helper';

// ==============================|| GUARD REPORTS - MARK AS VIOLATION ||============================== //

// Sentinel for "flag only, no linked account" — distinct from every real user_id.
const NO_ACCOUNT_VALUE = '__none__';

const IncidentReportViolationDialog = ({ open, report, orgId, onClose, onSaved }) => {
    const [selected, setSelected] = useState(NO_ACCOUNT_VALUE);
    const [submitting, setSubmitting] = useState(false);

    const candidates = (report?.parties || []).filter(p => p.user_id);

    useEffect(() => {
        if (!open) return;
        const defaultSubject = candidates.find(p => p.role === INCIDENT_PARTY_SUBJECT_ROLE);
        setSelected(defaultSubject?.user_id || NO_ACCOUNT_VALUE);
        // Re-derive only when the dialog opens (or opens for a different report).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, report?.id]);

    const handleConfirm = async () => {
        if (!report) return;
        setSubmitting(true);
        try {
            const userId = selected === NO_ACCOUNT_VALUE ? undefined : selected;
            const updated = await IncidentReportService.setViolation(
                orgId,
                report.id,
                userId
            );
            SnackbarNotify.success('Report flagged as a violation.');
            onSaved(updated);
            onClose();
        } catch (e) {
            SnackbarNotify.error(`Failed to flag violation - ${errorMessageFrom(e)}`);
        } finally {
            setSubmitting(false);
        }
    };

    if (!report) return null;

    return (
        <Dialog
            maxWidth="xs"
            fullWidth
            keepMounted
            TransitionComponent={PopupTransition}
            open={open}
            onClose={submitting ? undefined : onClose}
        >
            <DialogTitle>Mark as violation</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="secondary" sx={{ mb: 2 }}>
                    Choose the account this violation should appear on, or flag the report
                    with no linked account.
                </Typography>
                <RadioGroup value={selected} onChange={e => setSelected(e.target.value)}>
                    {candidates.map((p, index) => (
                        <FormControlLabel
                            key={`${p.user_id}-${index}`}
                            value={p.user_id}
                            control={<Radio />}
                            label={`${capitalizeFirstLetterMulti(p.name) || 'Unknown person'} — ${humanize(p.role)}`}
                        />
                    ))}
                    <FormControlLabel
                        value={NO_ACCOUNT_VALUE}
                        control={<Radio />}
                        label="No linked account — flag only"
                    />
                </RadioGroup>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
                <Button onClick={onClose} disabled={submitting} color="secondary">
                    Cancel
                </Button>
                <Button onClick={handleConfirm} disabled={submitting} variant="contained">
                    {submitting ? 'Saving…' : 'Set violation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default IncidentReportViolationDialog;
